-- | Canonical app state for the runtime.
module App.State
    ( RouteHistoryMode(..)
    , Step1LoadSession
    , Step1Session(..)
    , Step2ALoadSession
    , Step2ASession(..)
    , Step2BLoadSession
    , Step2BSession(..)
    , Step3Init
    , Step3LoadSession
    , Step3Session(..)
    , AppStateInit
    , AppState
    , step1InitForState
    , initialAppState
    , syncStepSessions
    , activateStep
    , setStep3Session
    ) where

import API.Document (Document)
import API.FormType (FormType)
import API.Types (DocumentID)
import App.Operation.Store (OperationStore, emptyOperationStore)
import App.Operation.Types (OperationId)
import App.Route as AppRoute
import Data.Maybe (Maybe(..))
import Error (ErrorWrapper)
import Form.StudentSelect as StudentSelect
import Routing.PushState (PushStateInterface)
import Steps.FormPicker as FormPicker
import Steps.Step1.State as Step1
import App.Step (Step(..))
import Steps.Step3.State (Step3State)
import Steps.WorkQueue.Cache (WorkQueueCache, getDoc)
import Steps.WorkQueue.State (FormSectionID, WorkQueueState)
import Steps.WorkQueue.UIState (WorkQueueUiState)

data RouteHistoryMode
    = PushRouteHistory
    | ReplaceRouteHistory

type Step3Init =
    { doc :: Maybe Document
    , formType :: Maybe FormType
    , initialAnchor :: Maybe FormSectionID
    }

type Step3LoadSession =
    { init :: Step3Init
    , opId :: Maybe OperationId
    }

type Step1LoadSession =
    { init :: Step1.InitialState
    , opId :: Maybe OperationId
    }

type Step2ALoadSession =
    { workQueueState :: WorkQueueState
    , workQueueCache :: WorkQueueCache
    , opId :: Maybe OperationId
    }

type Step2BLoadSession =
    { workQueueState :: WorkQueueState
    , workQueueUi :: WorkQueueUiState
    , workQueueCache :: WorkQueueCache
    , opId :: Maybe OperationId
    }

data Step1Session
    = Step1NotLoaded
    | Step1Loading Step1LoadSession
    | Step1Ready Step1.RecentFormState

-- Step 2 still uses the old view modules, but the app owns the first load and
-- export operations. These sessions keep those async results out of the view.
data Step2ASession
    = Step2ANotLoaded
    | Step2ALoading Step2ALoadSession
    | Step2AReady StudentSelect.SelectStudentState
    -- Some Step 2A failures cannot safely continue in the normal controls.
    -- Keep them as a page-level failure so the shared reload action can retry
    -- the current route.
    | Step2ALoadFailed ErrorWrapper

data Step2BSession
    = Step2BNotLoaded
    | Step2BLoading Step2BLoadSession
    | Step2BReady FormPicker.FormPickerState
    -- Some Step 2B failures cannot safely continue in the normal controls.
    -- Keep them as a page-level failure so the shared reload action can retry
    -- the current route.
    | Step2BLoadFailed ErrorWrapper

data Step3Session
    = Step3NotLoaded
    | Step3LoadingFormTypes Step3LoadSession
    | Step3LoadingInitialState Step3LoadSession
    | Step3Ready Step3State

type AppStateInit =
    { route :: AppRoute.Route
    , step :: Step
    , workQueueState :: WorkQueueState
    , workQueueUi :: WorkQueueUiState
    , workQueueCache :: WorkQueueCache
    , preselectForms :: Array DocumentID
    , pushStateInterface :: PushStateInterface
    }

type AppState =
    { route :: AppRoute.Route
    , step :: Step
    , workQueueState :: WorkQueueState
    , workQueueUi :: WorkQueueUiState
    , workQueueCache :: WorkQueueCache
    , preselectForms :: Array DocumentID
    , pushStateInterface :: PushStateInterface
    , operations :: OperationStore
    , step1 :: Step1Session
    , step2A :: Step2ASession
    , step2B :: Step2BSession
    , step3 :: Step3Session
    , routeVersion :: Int
    }

initialAppState :: AppStateInit -> AppState
initialAppState init =
    let state =
            { route: init.route
            , step: init.step
            , workQueueState: init.workQueueState
            , workQueueUi: init.workQueueUi
            , workQueueCache: init.workQueueCache
            , preselectForms: init.preselectForms
            , pushStateInterface: init.pushStateInterface
            , operations: emptyOperationStore
            , step1: Step1NotLoaded
            , step2A: Step2ANotLoaded
            , step2B: Step2BNotLoaded
            , step3: Step3NotLoaded
            , routeVersion: 0
            }
    in syncStepSessions state state

syncStepSessions :: AppState -> AppState -> AppState
syncStepSessions previous nextState =
    nextState
        { step1 = step1SessionForState previous nextState
        , step2A = step2ASessionForState nextState
        , step2B = step2BSessionForState nextState
        , step3 = step3SessionForState nextState
        }

activateStep :: Step -> AppState -> AppState
activateStep step state =
    syncStepSessions state (state { step = step })

setStep3Session :: Step3Session -> AppState -> AppState
setStep3Session step3 state = state { step3 = step3 }

step1SessionForState :: AppState -> AppState -> Step1Session
step1SessionForState previous nextState = case nextState.step of
    Step1 -> case previous.step1 of
        Step1Ready ready -> case previous.step of
            Step1 ->
                Step1Ready ready { searchState = nextState.workQueueUi.search }
            _ ->
                Step1Loading { init: step1InitForState nextState, opId: Nothing }
        _ ->
            Step1Loading { init: step1InitForState nextState, opId: Nothing }
    _ ->
        Step1NotLoaded

step2ASessionForState :: AppState -> Step2ASession
step2ASessionForState state = case state.step of
    Step2A ->
        Step2ALoading
            { workQueueState: state.workQueueState
            , workQueueCache: state.workQueueCache
            , opId: Nothing
            }
    _ ->
        Step2ANotLoaded

step2BSessionForState :: AppState -> Step2BSession
step2BSessionForState state = case state.step of
    Step2B ->
        Step2BLoading
            { workQueueState: state.workQueueState
            , workQueueUi: state.workQueueUi
            , workQueueCache: state.workQueueCache
            , opId: Nothing
            }
    _ ->
        Step2BNotLoaded

step3SessionForState :: AppState -> Step3Session
step3SessionForState state = case state.step of
    Step3 ->
        Step3LoadingFormTypes { init: step3InitForState state, opId: Nothing }
    Step3Doc _ ->
        Step3LoadingFormTypes { init: step3InitForState state, opId: Nothing }
    _ ->
        Step3NotLoaded

step3InitForState :: AppState -> Step3Init
step3InitForState state =
    { doc: currentDoc
    , formType: Nothing
    , initialAnchor: case state.route of
        AppRoute.RStep3 { section: AppRoute.SectionSet section } -> Just section
        _ -> Nothing
    }
  where
    currentDoc = case state.step of
        Step3Doc docId -> getDoc state.workQueueState docId
        _ -> Nothing

step1InitForState :: AppState -> Step1.InitialState
step1InitForState state =
    { workQueueState: state.workQueueState
    , workQueueUi: state.workQueueUi
    , workQueueCache: state.workQueueCache
    , preselectForms: state.preselectForms
    }
